import React from 'react';
import { Box, Divider, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  panel: {
    background: 'rgba(10, 14, 30, 0.88)',
    border: '1px solid rgba(77, 121, 255, 0.22)',
    borderRadius: 14,
    padding: 14,
    textAlign: 'left',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: 12,
    },
  },
  title: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea4d8',
    marginBottom: 10,
  },
  divider: {
    background: 'rgba(255,255,255,0.08)',
    marginBottom: 12,
  },
  section: {
    marginBottom: 12,
  },
  sectionTitle: {
    color: '#9ea4d8',
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 8,
  },
  row: {
    borderRadius: 12,
    border: '1px solid rgba(77, 121, 255, 0.18)',
    background: 'rgba(12, 18, 38, 0.85)',
    padding: '10px 12px',
    marginBottom: 8,
  },
  rowHeadline: {
    color: '#e5e7ff',
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1.45,
    wordBreak: 'break-word',
  },
  rowMeta: {
    color: '#8d93c9',
    fontSize: 11,
    lineHeight: 1.35,
    marginTop: 4,
  },
  empty: {
    color: '#8d93c9',
    fontSize: 12,
    lineHeight: 1.45,
  },
  footer: {
    color: '#9ea4d8',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 10,
  },
  error: {
    color: '#ffb4b4',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 8,
  },
}));

function formatUsdCompact(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(n);
}

function shortenHex(value) {
  const s = String(value || '').trim();
  if (!/^0x[0-9a-fA-F]+$/.test(s) || s.length < 12) return s || 'Unknown';
  return `${s.slice(0, 6)}...${s.slice(-4)}`;
}

function buildRows(activity) {
  const transfers = Array.isArray(activity && activity.largestTransfers) ? activity.largestTransfers : [];
  const smartWallets = Array.isArray(activity && activity.smartWalletActivity) ? activity.smartWalletActivity : [];
  const bridges = Array.isArray(activity && activity.bridgeFlows) ? activity.bridgeFlows : [];

  return {
    transfers: transfers.slice(0, 3).map((row, index) => ({
      key: row && row.tx_hash ? row.tx_hash : `transfer-${index}`,
      headline: row && row.summary ? String(row.summary) : `${shortenHex(row && row.wallet)} moved ${formatUsdCompact(row && row.amount_usd)}`,
      meta: row && row.ts ? String(row.ts) : (row && row.tx_hash ? shortenHex(row.tx_hash) : 'No timestamp'),
    })),
    smartWallets: smartWallets.slice(0, 3).map((row, index) => ({
      key: row && row.wallet ? `${row.wallet}-${index}` : `smart-${index}`,
      headline: row && row.summary ? String(row.summary) : `${shortenHex(row && row.wallet)} ${row && row.action ? row.action : 'active'} ${formatUsdCompact(row && row.amount_usd)}`,
      meta: row && row.ts ? String(row.ts) : (row && row.label ? String(row.label) : 'No timing info'),
    })),
    bridges: bridges.slice(0, 3).map((row, index) => ({
      key: row && row.bridge ? `${row.bridge}-${index}` : `bridge-${index}`,
      headline: row && row.summary ? String(row.summary) : `${row && row.direction ? row.direction : 'Bridge'} flow ${formatUsdCompact(row && row.amount_usd)}`,
      meta: row && row.ts ? String(row.ts) : (row && row.bridge ? String(row.bridge) : 'No bridge details'),
    })),
  };
}

function Section({ classes, title, rows, emptyText }) {
  return (
    <Box className={classes.section}>
      <Typography className={classes.sectionTitle}>{title}</Typography>
      {rows.length ? rows.map((row) => (
        <Box key={row.key} className={classes.row}>
          <Typography className={classes.rowHeadline}>{row.headline || 'No headline available'}</Typography>
          <Typography className={classes.rowMeta}>{row.meta || 'No context available'}</Typography>
        </Box>
      )) : <Typography className={classes.empty}>{emptyText}</Typography>}
    </Box>
  );
}

export default function OnChainActivityPanel({ activity, loading, error }) {
  const classes = useStyles();
  const rows = buildRows(activity);

  return (
    <Box className={classes.panel}>
      <Typography className={classes.title}>On-Chain Activity</Typography>
      <Divider className={classes.divider} />

      <Section
        classes={classes}
        title="Largest Transfers"
        rows={rows.transfers}
        emptyText={loading ? 'Loading transfer activity…' : 'No large transfers available.'}
      />

      <Section
        classes={classes}
        title="Smart Wallet Activity"
        rows={rows.smartWallets}
        emptyText={loading ? 'Loading smart-wallet activity…' : 'No smart-wallet events available.'}
      />

      <Section
        classes={classes}
        title="Bridge Flows"
        rows={rows.bridges}
        emptyText={loading ? 'Loading bridge flows…' : 'No bridge flow events available.'}
      />

      <Typography className={classes.footer}>
        {loading ? 'Refreshing on-chain activity feed…' : 'Displaying the latest activity payload passed into the panel.'}
      </Typography>
      {error ? <Typography className={classes.error}>{error}</Typography> : null}
    </Box>
  );
}
